import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { api, mutate } from '../shared/api';

type Locale = 'hr' | 'en';

type BankAccount = {
    id: number;
    parent_account_id: number | null;
    company_id: number | null;
    branch_dept_id: number | null;
    iban: string;
    bank_name: string;
    account_label: string;
    currency_code: string | null;
    purpose: 'main' | 'sub_zlato' | 'sub_mjenjacnica' | 'sub_eam' | 'unified' | 'other';
    lifecycle_status: 'active' | 'inactive' | 'pending_closure' | 'closed';
    is_active: number | boolean;
    is_fallback?: number | boolean;
    is_bank_primary?: number | boolean;
    last_used_at: string | null;
    notes: string | null;
    company_code?: string | null;
    company_name?: string | null;
    branch_name?: string | null;
    branch_code?: string | null;
    parent_label?: string | null;
    parent_iban?: string | null;
};

type Company = { id: number; code: string; name: string };
type Department = { id: number; code: string | null; name: string; company_id: number };

// IBAN → banka (7-znamenkasti kod iz HRkk[kod]…) — autodetekcija u formi računa.
const BANK_BY_CODE: Record<string, string> = {
    '2340009': 'Privredna banka Zagreb d.d.',
    '2402006': 'Erste&Steiermärkische Bank d.d.',
    '2407000': 'OTP banka d.d.',
    '2484008': 'Raiffeisenbank Austria d.d.',
    '2360000': 'Zagrebačka banka d.d.',
    '2390001': 'Hrvatska poštanska banka d.d.',
    '2500009': 'Addiko Bank d.d.',
};
function bankFromIban(iban: string): string | null {
    const n = (iban || '').replace(/\s+/g, '').toUpperCase();
    if (!n.startsWith('HR') || n.length < 11) return null;
    return BANK_BY_CODE[n.slice(4, 11)] ?? null;
}

const PURPOSE_META: Record<BankAccount['purpose'], { hr: string; en: string; color: string; bg: string }> = {
    main: { hr: 'GLAVNI', en: 'MAIN', color: '#fff', bg: '#1e40af' },
    sub_zlato: { hr: 'Podračun zlato', en: 'Sub gold', color: 'var(--ad-ink)', bg: '#fff7e0' },
    sub_mjenjacnica: { hr: 'Podračun mjenjačnica', en: 'Sub exchange', color: 'var(--ad-ink)', bg: '#fff7e0' },
    sub_eam: { hr: 'Podračun EAM', en: 'Sub EAM', color: '#fff', bg: '#b14040' },
    unified: { hr: 'UJEDINJENI (jedan po poslovnici)', en: 'UNIFIED', color: '#fff', bg: '#1f7a36' },
    other: { hr: 'Ostalo', en: 'Other', color: '#444', bg: '#eee' },
};

const STATUS_META: Record<BankAccount['lifecycle_status'], { hr: string; en: string; color: string }> = {
    active: { hr: '✓ Aktivan', en: '✓ Active', color: '#1f7a36' },
    inactive: { hr: '○ Neaktivan', en: '○ Inactive', color: '#888' },
    pending_closure: { hr: '⚠ Za gašenje', en: '⚠ Closing', color: 'var(--ad-acc)' },
    closed: { hr: '✕ Zatvoren', en: '✕ Closed', color: '#b14040' },
};

const fmtDate = (s: string | null): string => {
    if (!s) return '—';
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
    return m ? `${m[3]}.${m[2]}.${m[1]}.` : s;
};

const ibanMasked = (iban: string): string => {
    if (!iban || iban.length < 8) return iban;
    return iban.slice(0, 4) + '…' + iban.slice(-4);
};

export function AdminBankAccountsSection({ token, locale }: { token: string; locale: Locale }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [rows, setRows] = useState<BankAccount[]>([]);
    const [companies, setCompanies] = useState<Company[]>([]);
    const [departments, setDepartments] = useState<Department[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [filterBank, setFilterBank] = useState('');
    const [filterStatus, setFilterStatus] = useState<string>('active');
    const [filterPurpose, setFilterPurpose] = useState<string>('');
    const [filterCompany, setFilterCompany] = useState<string>('');
    const [filterBranch, setFilterBranch] = useState<string>('');
    const [filterFallback, setFilterFallback] = useState<boolean>(false);
    const [search, setSearch] = useState<string>('');
    const [showForm, setShowForm] = useState<BankAccount | 'new' | null>(null);
    const [showCleanup, setShowCleanup] = useState(false);

    const load = useCallback(async () => {
        setLoading(true); setError('');
        try {
            const params = new URLSearchParams();
            if (filterBank) params.set('bank', filterBank);
            if (filterStatus) params.set('status', filterStatus);
            if (filterPurpose) params.set('purpose', filterPurpose);
            if (filterCompany) params.set('company_id', filterCompany);
            if (filterBranch) params.set('branch_dept_id', filterBranch);
            if (filterFallback) params.set('is_fallback', '1');
            if (search.trim()) params.set('q', search.trim());
            const r = await api<{ rows: BankAccount[] }>(`/admin/bank-accounts?${params.toString()}`, token);
            setRows(r.rows ?? []);
        } catch (e) { setError(e instanceof Error ? e.message : 'API'); }
        finally { setLoading(false); }
    }, [token, filterBank, filterStatus, filterPurpose, filterCompany, filterBranch, filterFallback, search]);

    useEffect(() => { void load(); }, [load]);
    useEffect(() => {
        if (!token) return;
        void api<{ companies: Company[] }>('/foundation', token).then((r) => setCompanies(r.companies ?? [])).catch(() => undefined);
        void api<{ departments: Department[] }>('/admin/structure', token).then((r) => setDepartments(r.departments ?? [])).catch(() => undefined);
    }, [token]);

    const banks = useMemo(() => Array.from(new Set(rows.map((r) => r.bank_name))).sort(), [rows]);
    const aggregates = useMemo(() => {
        const byPurpose: Record<string, number> = {};
        rows.forEach((r) => { byPurpose[r.purpose] = (byPurpose[r.purpose] ?? 0) + 1; });
        return byPurpose;
    }, [rows]);

    // Debounce pretrage (IBAN/oznaka/poslovnica/bilješke) — ne šalji request na svaki znak.
    const [searchInput, setSearchInput] = useState('');
    useEffect(() => { const h = window.setTimeout(() => setSearch(searchInput), 300); return () => window.clearTimeout(h); }, [searchInput]);
    // Poslovnice u dropdownu — kaskadno po odabranoj firmi, sortirano po imenu.
    const branchOptions = useMemo(() => {
        const list = filterCompany ? departments.filter((d) => String(d.company_id) === filterCompany) : departments;
        return [...list].sort((a, b) => a.name.localeCompare(b.name, 'hr'));
    }, [departments, filterCompany]);
    const resetFilters = () => { setFilterBank(''); setFilterCompany(''); setFilterBranch(''); setFilterPurpose(''); setFilterStatus('active'); setFilterFallback(false); setSearchInput(''); setSearch(''); };

    return (
        <div style={{ padding: '0 8px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
                <div>
                    <h3 style={{ margin: 0, color: 'var(--ad-ink)' }}>{t('Bankovni računi i podračuni', 'Bank accounts and sub-accounts')}</h3>
                    <p className="muted" style={{ margin: '2px 0 0', fontSize: '0.8rem' }}>
                        {t('Foundation za m.21 Platni promet. Glavni računi (PBZ/ERSTE) + podračuni po poslovnicama.', 'Foundation for m.21 Payment traffic. Main accounts + branch sub-accounts.')}
                    </p>
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                    <button type="button" onClick={() => setShowCleanup(true)}
                        style={{ background: 'transparent', color: 'var(--ad-acc)', border: '1px solid var(--ad-acc)', borderRadius: 6, padding: '5px 10px', cursor: 'pointer', fontSize: '0.82rem', fontWeight: 700 }}>
                        🧹 {t('Kandidati za gašenje', 'Cleanup candidates')}
                    </button>
                    <button type="button" onClick={() => setShowForm('new')}
                        style={{ background: '#1f7a36', color: '#fff', border: 0, borderRadius: 6, padding: '5px 14px', cursor: 'pointer', fontSize: '0.82rem', fontWeight: 700 }}>
                        + {t('Novi podračun', 'New sub-account')}
                    </button>
                </div>
            </div>

            {/* Quick stats */}
            <div style={{ display: 'flex', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
                {Object.entries(aggregates).map(([k, v]) => {
                    const meta = PURPOSE_META[k as BankAccount['purpose']] ?? PURPOSE_META.other;
                    return (
                        <div key={k} style={{ background: meta.bg, color: meta.color, padding: '4px 12px', borderRadius: 6, fontSize: '0.78rem', fontWeight: 700 }}>
                            {locale === 'en' ? meta.en : meta.hr}: {v}
                        </div>
                    );
                })}
                <div style={{ background: '#eee', padding: '4px 12px', borderRadius: 6, fontSize: '0.78rem', fontWeight: 700, color: '#444' }}>
                    {t('Ukupno:', 'Total:')} {rows.length}
                </div>
            </div>

            {/* Filteri */}
            <div style={{ display: 'flex', gap: 10, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center', padding: '8px 10px', background: '#fafaf6', borderRadius: 8 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.78rem' }}>
                    {t('Banka', 'Bank')}:
                    <select value={filterBank} onChange={(e) => setFilterBank(e.target.value)} style={{ padding: '2px 6px', fontSize: '0.78rem' }}>
                        <option value="">{t('— sve —', '— all —')}</option>
                        {banks.map((b) => <option key={b} value={b}>{b}</option>)}
                    </select>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.78rem' }}>
                    {t('Firma', 'Company')}:
                    <select value={filterCompany} onChange={(e) => setFilterCompany(e.target.value)} style={{ padding: '2px 6px', fontSize: '0.78rem' }}>
                        <option value="">{t('— sve —', '— all —')}</option>
                        {companies.map((c) => <option key={c.id} value={c.id}>{c.code}</option>)}
                    </select>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.78rem' }}>
                    {t('Tip', 'Purpose')}:
                    <select value={filterPurpose} onChange={(e) => setFilterPurpose(e.target.value)} style={{ padding: '2px 6px', fontSize: '0.78rem' }}>
                        <option value="">{t('— sve —', '— all —')}</option>
                        {(Object.entries(PURPOSE_META) as Array<[BankAccount['purpose'], typeof PURPOSE_META.main]>).map(([k, m]) =>
                            <option key={k} value={k}>{locale === 'en' ? m.en : m.hr}</option>
                        )}
                    </select>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.78rem' }}>
                    {t('Poslovnica', 'Branch')}:
                    <select value={filterBranch} onChange={(e) => setFilterBranch(e.target.value)} style={{ padding: '2px 6px', fontSize: '0.78rem', maxWidth: 200 }}>
                        <option value="">{t('— sve —', '— all —')}</option>
                        {branchOptions.map((d) => <option key={d.id} value={d.id}>{d.name}{d.code ? ` (${d.code})` : ''}</option>)}
                    </select>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.78rem' }}>
                    {t('Status', 'Status')}:
                    <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} style={{ padding: '2px 6px', fontSize: '0.78rem' }}>
                        <option value="">{t('— sve —', '— all —')}</option>
                        {(Object.entries(STATUS_META) as Array<[BankAccount['lifecycle_status'], typeof STATUS_META.active]>).map(([k, m]) =>
                            <option key={k} value={k}>{locale === 'en' ? m.en : m.hr}</option>
                        )}
                    </select>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.78rem' }}>
                    🔎
                    <input type="text" value={searchInput} onChange={(e) => setSearchInput(e.target.value)} placeholder={t('IBAN / oznaka / poslovnica', 'IBAN / label / branch')}
                        style={{ padding: '3px 8px', fontSize: '0.78rem', width: 190 }} />
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.78rem', cursor: 'pointer' }} title={t('Prikaži samo fallback (⭐) račune', 'Only fallback (⭐) accounts')}>
                    <input type="checkbox" checked={filterFallback} onChange={(e) => setFilterFallback(e.target.checked)} />
                    ⭐ {t('Fallback', 'Fallback')}
                </label>
                <button type="button" onClick={resetFilters} style={{ padding: '3px 10px', fontSize: '0.76rem', background: 'transparent', border: '1px solid #ccc', borderRadius: 6, cursor: 'pointer' }}>
                    ↺ {t('Reset', 'Reset')}
                </button>
                <span style={{ fontSize: '0.76rem', color: '#888', marginLeft: 'auto' }}>{rows.length} {t('računa', 'accounts')}</span>
            </div>

            {error ? <div style={{ color: '#b14040', background: '#fde8e8', padding: 8, borderRadius: 6, marginBottom: 8 }}>⚠ {error}</div> : null}
            {loading ? <p className="muted">{t('Učitavam…', 'Loading…')}</p> : null}

            {!loading && rows.length === 0 ? (
                <p className="muted" style={{ padding: 12 }}>{t('Nema redaka za zadane filtre.', 'No accounts match these filters.')}</p>
            ) : null}

            {!loading && rows.length > 0 ? (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                    <thead>
                        <tr style={{ background: '#fafaf6', color: 'var(--ad-ink)', textAlign: 'left' }}>
                            <th style={{ padding: '6px 8px' }}>{t('Banka', 'Bank')}</th>
                            <th style={{ padding: '6px 8px' }}>{t('IBAN', 'IBAN')}</th>
                            <th style={{ padding: '6px 8px' }}>{t('Naziv', 'Label')}</th>
                            <th style={{ padding: '6px 8px' }}>{t('Firma', 'Co')}</th>
                            <th style={{ padding: '6px 8px' }}>{t('Poslovnica', 'Branch')}</th>
                            <th style={{ padding: '6px 8px' }}>{t('Tip', 'Purpose')}</th>
                            <th style={{ padding: '6px 8px' }}>{t('Status', 'Status')}</th>
                            <th style={{ padding: '6px 8px' }}>{t('Zadnje korišteno', 'Last used')}</th>
                            <th style={{ padding: '6px 8px' }}></th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r) => {
                            const pm = PURPOSE_META[r.purpose] ?? PURPOSE_META.other;
                            const sm = STATUS_META[r.lifecycle_status] ?? STATUS_META.inactive;
                            return (
                                <tr key={r.id} style={{ borderTop: '1px solid #eee', cursor: 'pointer' }} onClick={() => setShowForm(r)}>
                                    <td style={{ padding: '5px 8px', whiteSpace: 'nowrap' }}>{r.bank_name}</td>
                                    <td style={{ padding: '5px 8px', fontFamily: 'ui-monospace, monospace' }}>{ibanMasked(r.iban)}</td>
                                    <td style={{ padding: '5px 8px' }}>{r.account_label}</td>
                                    <td style={{ padding: '5px 8px' }}>{r.company_code ?? '—'}</td>
                                    <td style={{ padding: '5px 8px' }}>{r.branch_name ?? '—'}</td>
                                    <td style={{ padding: '5px 8px' }}>
                                        <span style={{ background: pm.bg, color: pm.color, padding: '1px 7px', borderRadius: 4, fontSize: '0.72rem', fontWeight: 700 }}>
                                            {locale === 'en' ? pm.en : pm.hr}
                                        </span>
                                    </td>
                                    <td style={{ padding: '5px 8px', color: sm.color, fontWeight: 700 }}>{locale === 'en' ? sm.en : sm.hr}</td>
                                    <td style={{ padding: '5px 8px', color: '#888' }}>{fmtDate(r.last_used_at)}</td>
                                    <td style={{ padding: '5px 8px', textAlign: 'right' }}>
                                        <button type="button" onClick={(e) => { e.stopPropagation(); setShowForm(r); }}
                                            style={{ background: 'transparent', color: 'var(--ad-ink)', border: '1px solid var(--ad-acc)', borderRadius: 4, padding: '1px 6px', fontSize: '0.72rem', cursor: 'pointer' }}>
                                            {t('uredi', 'edit')}
                                        </button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            ) : null}

            {showForm ? <BankAccountForm row={showForm === 'new' ? null : showForm} token={token} locale={locale} companies={companies} departments={departments} accounts={rows} onClose={(saved) => { setShowForm(null); if (saved) void load(); }} /> : null}
            {showCleanup ? <CleanupCandidatesModal token={token} locale={locale} onClose={() => setShowCleanup(false)} onMarkClosed={() => void load()} /> : null}
        </div>
    );
}

function BankAccountForm({ row, token, locale, companies, departments, accounts, onClose }: { row: BankAccount | null; token: string; locale: Locale; companies: Company[]; departments: Department[]; accounts: BankAccount[]; onClose: (saved: boolean) => void }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [form, setForm] = useState({
        parent_account_id: row?.parent_account_id ?? null,
        company_id: row?.company_id ?? null,
        branch_dept_id: row?.branch_dept_id ?? null,
        iban: row?.iban ?? '',
        bank_name: row?.bank_name ?? 'PBZ',
        account_label: row?.account_label ?? '',
        currency_code: row?.currency_code ?? 'EUR',
        purpose: row?.purpose ?? 'sub_zlato',
        lifecycle_status: row?.lifecycle_status ?? 'active',
        is_fallback: row?.is_fallback ? true : false,
        is_bank_primary: row?.is_bank_primary ? true : false,
        notes: row?.notes ?? '',
    });
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState('');
    // Poslovnica NIJE vezana uz firmu — ADMS obavlja poslove za druge firme, pa poslovnica (npr. PULA 01)
    // može imati podračun registriran pod bilo kojom firmom. Zato prikaži SVE poslovnice, neovisno o firmi.
    const filteredDepts = useMemo(() => [...departments].sort((a, b) => (a.code ?? a.name).localeCompare(b.code ?? b.name)), [departments]);
    const possibleParents = useMemo(() => accounts.filter((a) => a.purpose === 'main' && (!form.company_id || a.company_id === form.company_id)), [accounts, form.company_id]);
    const bankOptions = useMemo(() => {
        const std = ['Privredna banka Zagreb d.d.', 'Erste&Steiermärkische Bank d.d.', 'OTP banka d.d.', 'Raiffeisenbank Austria d.d.', 'Zagrebačka banka d.d.', 'Hrvatska poštanska banka d.d.', 'Addiko Bank d.d.'];
        return Array.from(new Set([...accounts.map((a) => a.bank_name).filter(Boolean), ...std, ...(form.bank_name ? [form.bank_name] : [])])).sort();
    }, [accounts, form.bank_name]);

    const save = async () => {
        setSaving(true); setErr('');
        try {
            const body: any = { ...form };
            if (!body.notes) delete body.notes;
            if (row) {
                await mutate(`/admin/bank-accounts/${row.id}`, token, 'PATCH', body);
            } else {
                await mutate('/admin/bank-accounts', token, 'POST', body);
            }
            onClose(true);
        } catch (e) { setErr(e instanceof Error ? e.message : 'API'); }
        finally { setSaving(false); }
    };

    const del = async () => {
        if (!row) return;
        if (!confirm(t('Obrisati ovaj račun?', 'Delete this account?'))) return;
        setSaving(true);
        try { await mutate(`/admin/bank-accounts/${row.id}`, token, 'DELETE', {}); onClose(true); }
        catch (e) { setErr(e instanceof Error ? e.message : 'API'); }
        finally { setSaving(false); }
    };

    return (
        <div onClick={() => onClose(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
            <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: 10, padding: 20, maxWidth: 600, width: '100%', maxHeight: '90vh', overflowY: 'auto' }}>
                <h3 style={{ margin: 0, color: 'var(--ad-ink)' }}>{row ? t('Uredi račun', 'Edit account') : t('Novi račun / podračun', 'New account')}</h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 14 }}>
                    <label>{t('Banka', 'Bank')}
                        <select value={form.bank_name} onChange={(e) => setForm({ ...form, bank_name: e.target.value })} style={{ width: '100%', padding: 6 }}>
                            <option value="">{t('— odaberi banku —', '— pick bank —')}</option>
                            {bankOptions.map((b) => <option key={b} value={b}>{b}</option>)}
                        </select>
                    </label>
                    <label>{t('IBAN', 'IBAN')}
                        <input type="text" value={form.iban} onChange={(e) => setForm({ ...form, iban: e.target.value.toUpperCase() })}
                            onBlur={(e) => { const bn = bankFromIban(e.target.value); if (bn) setForm((f) => ({ ...f, bank_name: bn })); }}
                            placeholder="HR..." style={{ width: '100%', padding: 6, fontFamily: 'ui-monospace, monospace' }} />
                    </label>
                    <label>{t('Naziv računa (kratko)', 'Label')}
                        <input type="text" value={form.account_label} onChange={(e) => setForm({ ...form, account_label: e.target.value })} placeholder={t('npr. Rijeka Medulićeva', 'e.g. Rijeka Medulićeva')} style={{ width: '100%', padding: 6 }} />
                    </label>
                    <label>{t('Valuta', 'Currency')}
                        <input type="text" value={form.currency_code ?? 'EUR'} onChange={(e) => setForm({ ...form, currency_code: e.target.value })} maxLength={3} style={{ width: '100%', padding: 6 }} />
                    </label>
                    <label>{t('Firma', 'Company')}
                        <select value={form.company_id ?? ''} onChange={(e) => setForm({ ...form, company_id: e.target.value ? parseInt(e.target.value, 10) : null })} style={{ width: '100%', padding: 6 }}>
                            <option value="">{t('— odaberi —', '— pick —')}</option>
                            {companies.map((c) => <option key={c.id} value={c.id}>{c.code} · {c.name}</option>)}
                        </select>
                    </label>
                    <label>{t('Poslovnica', 'Branch')} <span style={{ fontSize: '0.68rem', color: '#999', fontWeight: 400 }}>({t('bilo koja, neovisno o firmi', 'any, independent of company')})</span>
                        <select value={form.branch_dept_id ?? ''} onChange={(e) => setForm({ ...form, branch_dept_id: e.target.value ? parseInt(e.target.value, 10) : null })} style={{ width: '100%', padding: 6 }}>
                            <option value="">{t('— bez poslovnice —', '— none —')}</option>
                            {filteredDepts.map((d) => { const cc = companies.find((c) => c.id === d.company_id)?.code; return <option key={d.id} value={d.id}>{d.code ? d.code + ' · ' : ''}{d.name}{cc ? ' (' + cc + ')' : ''}</option>; })}
                        </select>
                    </label>
                    <label>{t('Tip', 'Purpose')}
                        <select value={form.purpose} onChange={(e) => setForm({ ...form, purpose: e.target.value as any })} style={{ width: '100%', padding: 6 }}>
                            {(Object.entries(PURPOSE_META) as Array<[BankAccount['purpose'], typeof PURPOSE_META.main]>).map(([k, m]) =>
                                <option key={k} value={k}>{locale === 'en' ? m.en : m.hr}</option>
                            )}
                        </select>
                    </label>
                    <label>{t('Status', 'Status')}
                        <select value={form.lifecycle_status} onChange={(e) => setForm({ ...form, lifecycle_status: e.target.value as any })} style={{ width: '100%', padding: 6 }}>
                            {(Object.entries(STATUS_META) as Array<[BankAccount['lifecycle_status'], typeof STATUS_META.active]>).map(([k, m]) =>
                                <option key={k} value={k}>{locale === 'en' ? m.en : m.hr}</option>
                            )}
                        </select>
                    </label>
                    {form.purpose !== 'main' ? (
                        <label style={{ gridColumn: 'span 2' }}>{t('Parent (glavni) račun', 'Parent (main) account')}
                            <select value={form.parent_account_id ?? ''} onChange={(e) => setForm({ ...form, parent_account_id: e.target.value ? parseInt(e.target.value, 10) : null })} style={{ width: '100%', padding: 6 }}>
                                <option value="">{t('— bez parenta —', '— none —')}</option>
                                {possibleParents.map((p) => <option key={p.id} value={p.id}>{p.bank_name} · {p.account_label} ({p.iban})</option>)}
                            </select>
                        </label>
                    ) : null}
                    <div style={{ gridColumn: 'span 2', display: 'flex', flexDirection: 'column', gap: 8, background: '#faf6ec', border: '1px solid #efe6d2', borderRadius: 8, padding: '10px 12px' }}>
                        <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer' }}>
                            <input type="checkbox" checked={!!form.is_bank_primary} onChange={(e) => setForm({ ...form, is_bank_primary: e.target.checked })} style={{ marginTop: 3 }} />
                            <span><strong>🏦 {t('Glavni podračun na ovoj banci', 'Main sub-account at this bank')}</strong><br />
                                <span style={{ fontSize: '0.74rem', color: '#8a7a52' }}>{t('Podračun za manipulaciju novcem (nije vezan za poslovnicu) — glavni na ovoj banci. Jedan po firmi+banci. Izvor za interni virman → podračun poslovnice.', 'Money-manipulation sub-account (not branch-tied) — main at this bank. One per company+bank.')}</span></span>
                        </label>
                        <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer' }}>
                            <input type="checkbox" checked={!!form.is_fallback} onChange={(e) => setForm({ ...form, is_fallback: e.target.checked })} style={{ marginTop: 3 }} />
                            <span><strong>⭐ {t('Glavni-glavni podračun (zadani / fallback)', 'Master sub-account (default / fallback)')}</strong><br />
                                <span style={{ fontSize: '0.74rem', color: '#8a7a52' }}>{t('Od svih glavnih podračuna — ovaj je zadani (fallback) za cijelu firmu. S njega idu isplate kad nema podudaranja po banci. Jedan po firmi.', 'The default (fallback) among all main sub-accounts, per company.')}</span></span>
                        </label>
                    </div>
                    <label style={{ gridColumn: 'span 2' }}>{t('Bilješke', 'Notes')}
                        <textarea value={form.notes ?? ''} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} style={{ width: '100%', padding: 6, resize: 'vertical', fontFamily: 'inherit' }} />
                    </label>
                </div>

                {err ? <div style={{ color: '#b14040', marginTop: 8, fontSize: '0.84rem' }}>⚠ {err}</div> : null}

                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 16 }}>
                    {row ? (
                        <button type="button" disabled={saving} onClick={() => void del()} style={{ background: 'transparent', border: '1px solid #b14040', color: '#b14040', borderRadius: 6, padding: '6px 14px', cursor: 'pointer', fontWeight: 600 }}>
                            🗑 {t('Obriši', 'Delete')}
                        </button>
                    ) : <span />}
                    <div style={{ display: 'flex', gap: 8 }}>
                        <button type="button" onClick={() => onClose(false)} style={{ background: 'transparent', border: '1px solid #888', borderRadius: 6, padding: '6px 14px', cursor: 'pointer' }}>{t('Odustani', 'Cancel')}</button>
                        <button type="button" disabled={saving} onClick={() => void save()} style={{ background: '#1f7a36', color: '#fff', border: 0, borderRadius: 6, padding: '6px 16px', cursor: 'pointer', fontWeight: 700 }}>
                            {saving ? t('Spremam…', 'Saving…') : t('💾 Spremi', '💾 Save')}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

function CleanupCandidatesModal({ token, locale, onClose, onMarkClosed }: { token: string; locale: Locale; onClose: () => void; onMarkClosed: () => void }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [rows, setRows] = useState<any[] | null>(null);
    const [threshold, setThreshold] = useState('');
    const [busy, setBusy] = useState<number | null>(null);

    const load = useCallback(async () => {
        try {
            const r = await api<{ rows: any[]; threshold: string }>('/admin/bank-accounts/cleanup-candidates', token);
            setRows(r.rows); setThreshold(r.threshold);
        } catch { setRows([]); }
    }, [token]);
    useEffect(() => { void load(); }, [load]);

    const markForClosure = async (id: number) => {
        setBusy(id);
        try { await mutate(`/admin/bank-accounts/${id}`, token, 'PATCH', { lifecycle_status: 'pending_closure' }); await load(); onMarkClosed(); }
        catch { /* */ } finally { setBusy(null); }
    };

    return (
        <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
            <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: 10, padding: 20, maxWidth: 900, width: '100%', maxHeight: '90vh', overflowY: 'auto' }}>
                <h3 style={{ margin: 0, color: 'var(--ad-acc)' }}>🧹 {t('Kandidati za gašenje', 'Cleanup candidates')}</h3>
                <p className="muted" style={{ fontSize: '0.82rem', marginTop: 4 }}>
                    {t('Računi koji su aktivni ali se nisu koristili otkad', 'Accounts active but unused since')} <strong>{fmtDate(threshold || null)}</strong> ({t('6+ mjeseci', '6+ months')}).
                </p>

                {rows == null ? <p className="muted">{t('Učitavam…', 'Loading…')}</p>
                : rows.length === 0 ? <p className="muted" style={{ padding: 12 }}>{t('Nema kandidata za gašenje — sve aktivno u zadnjih 6 mjeseci.', 'No candidates — all active in the last 6 months.')}</p>
                : (
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem', marginTop: 8 }}>
                        <thead>
                            <tr style={{ background: '#fafaf6', color: 'var(--ad-ink)', textAlign: 'left' }}>
                                <th style={{ padding: '6px 8px' }}>{t('Banka', 'Bank')}</th>
                                <th style={{ padding: '6px 8px' }}>{t('IBAN', 'IBAN')}</th>
                                <th style={{ padding: '6px 8px' }}>{t('Naziv', 'Label')}</th>
                                <th style={{ padding: '6px 8px' }}>{t('Firma', 'Co')}</th>
                                <th style={{ padding: '6px 8px' }}>{t('Poslovnica', 'Branch')}</th>
                                <th style={{ padding: '6px 8px' }}>{t('Zadnje korišteno', 'Last used')}</th>
                                <th style={{ padding: '6px 8px' }}></th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r) => (
                                <tr key={r.id} style={{ borderTop: '1px solid #eee' }}>
                                    <td style={{ padding: '5px 8px' }}>{r.bank_name}</td>
                                    <td style={{ padding: '5px 8px', fontFamily: 'ui-monospace, monospace' }}>{ibanMasked(r.iban)}</td>
                                    <td style={{ padding: '5px 8px' }}>{r.account_label}</td>
                                    <td style={{ padding: '5px 8px' }}>{r.company_name ?? '—'}</td>
                                    <td style={{ padding: '5px 8px' }}>{r.branch_name ?? '—'}</td>
                                    <td style={{ padding: '5px 8px', color: '#888' }}>{fmtDate(r.last_used_at)}</td>
                                    <td style={{ padding: '5px 8px', textAlign: 'right' }}>
                                        <button type="button" disabled={busy === r.id} onClick={() => void markForClosure(r.id)}
                                            style={{ background: 'var(--ad-acc)', color: '#fff', border: 0, borderRadius: 4, padding: '2px 10px', fontSize: '0.74rem', cursor: 'pointer', fontWeight: 700 }}>
                                            {busy === r.id ? '…' : t('⚠ Za gašenje', '⚠ Mark for closure')}
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}

                <div style={{ marginTop: 16, textAlign: 'right' }}>
                    <button type="button" onClick={onClose} style={{ background: 'var(--ad-ink)', color: '#fff', border: 0, borderRadius: 6, padding: '6px 14px', cursor: 'pointer' }}>{t('Zatvori', 'Close')}</button>
                </div>
            </div>
        </div>
    );
}
